import React, {useMemo} from 'react';
import {Divider, Stack} from '@mui/material';
import {Flow} from '@popularpays/analytics/dist/providers/delta/Helper';
import {Button} from '@lightricks/react-design-system';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import Sentry from '@/services/sentry/Sentry';
import {flowNames} from '@/lib/delta/deltaConstants.js';
import translate from '@/utils/translate';
import {getEnv} from '@/config/environment';
import {AuthMode} from '@/types/AuthModes';
import {Product} from '@/types/product';
import useTrackAuthStepEvent from '@/hooks/analytics/use-track-auth-step-event';
import styles from './OauthSection.module.scss';

const googleIcon = '/assets/svg/icon-google.svg';
const appleIcon = '/assets/svg/icon-apple.svg';
const facebookIcon = '/assets/svg/icon-facebook.svg';

export type OauthOptions = {
  google?: boolean;
  apple?: boolean;
  facebook?: boolean;
};

type OauthSectionProps = {
  product?: Product;
  oauthOptions?: OauthOptions;
  setError: ({title, message}: {title: string; message: string}) => void;
  mode: AuthMode;
};

type OauthParams = {
  product: Product | undefined;
  flowEvent: Flow;
  setError: OauthSectionProps['setError'];
  provider: keyof OauthOptions;
  mode: AuthMode;
  trackAuthStepStarted: (params: {
    flow_id: string;
    process_id: string;
    provider: string;
    request_type: string;
    input_value: string;
    number_of_required_fields?: number;
    number_of_fields_completed?: number;
  }) => void;
};

const authWithProvider =
  ({
    product,
    flowEvent,
    setError,
    provider,
    mode,
    trackAuthStepStarted,
  }: OauthParams) =>
  async () => {
    const {flow_id} = flowEvent;
    trackAuthStepStarted({
      flow_id: flow_id || '',
      process_id: '',
      provider,
      request_type: '',
      input_value: '',
      number_of_required_fields: mode === 'signup' ? 0 : undefined,
      number_of_fields_completed: mode === 'signup' ? 0 : undefined,
    });

    const fortressBase =
      getEnv().VITE_FORTRESS_API_BASE_URI ||
      'https://api.fortress-ww-prd.lightricks.com';

    const appName =
      getEnv().VITE_FORTRESS_APP_NAME || 'com.lightricks.popular-pays.brands';

    const apiUrl = getEnv().VITE_API_HOST || window.location.origin;

    const queryParams = new URLSearchParams();

    queryParams.append('provider', provider);

    if (product) queryParams.append('signupType', product);

    const tokenCallbackUri = `${apiUrl}/authentication/dashboard_user/sign_in?${queryParams.toString()}`;

    let requestUrl = `${fortressBase}/v2/auth/oauth/${provider}?app=${encodeURIComponent(
      appName
    )}&plt=w&token_callback_uri=${encodeURIComponent(tokenCallbackUri)}`;

    // Add sandbox environment flag for non-production deployments
    if (!['production', 'staging'].includes(getEnv().MODE)) {
      requestUrl += '&env=sandbox';
    }

    try {
      const response = await fetch(requestUrl, {
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(
          `Fortress OAuth request failed (status ${response.status})`
        );
      }

      const data = await response.json();
      const {authorizationUrl} = data;
      if (!authorizationUrl) {
        throw new Error('authorizationUrl not found in Fortress response');
      }
      try {
        await AnalyticsService.dischargeEventBatch();
      } catch (error) {
        Sentry.captureException(error as object, {
          title: 'Analytics Discharge Error',
          error,
        });
      }
      if (window.top) {
        window.top.location.href = authorizationUrl;
      } else {
        window.location.href = authorizationUrl;
      }
    } catch (error) {
      Sentry.captureException(error as object, {
        title: `${provider} OAuth Init Error`,
        error,
      });

      setError({
        title: 'Sorry, something went wrong',
        message:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
      });
    }
  };

function OauthSection({
  oauthOptions,
  product,
  setError,
  mode,
}: OauthSectionProps) {
  const {started: trackAuthStepStarted} = useTrackAuthStepEvent(mode);
  const flowEvent =
    AnalyticsService.getFlow(
      mode === 'signup' ? flowNames.AUTH.NEW_SIGNUP : flowNames.AUTH.LOGIN
    ) || {};

  const providerIcons: Record<string, {src: string; alt: string}> = {
    google: {src: googleIcon, alt: 'Google'},
    apple: {src: appleIcon, alt: 'Apple'},
    facebook: {src: facebookIcon, alt: 'Facebook'},
  };

  const getProviderIcon = (provider: string) => {
    const icon = providerIcons[provider];
    return icon ? (
      <img src={icon.src} alt={icon.alt} width={24} height={24} />
    ) : null;
  };

  type Provider = keyof typeof providerIcons;

  const providerTextMap = useMemo(() => {
    const textMap: Record<Provider, string> = {
      google: translate(
        mode === 'signup' ? 'Sign Up With Google' : 'Log In With Google'
      ),
      apple: translate(
        mode === 'signup' ? 'Sign Up With Apple' : 'Log In With Apple'
      ),
      facebook: translate(
        mode === 'signup' ? 'Sign Up With Facebook' : 'Log In With Facebook'
      ),
    };
    return textMap;
  }, [mode]);

  if (!oauthOptions) {
    return null;
  }

  return (
    <Stack spacing="24px" marginBottom="24px">
      {oauthOptions?.google && (
        <Button
          onClick={authWithProvider({
            product,
            flowEvent,
            setError,
            provider: 'google',
            mode,
            trackAuthStepStarted,
          })}
          appearance="neutral"
          mode="plain"
          size="large"
          className={styles.oauthButton}
          startIcon={getProviderIcon('google')}
        >
          {providerTextMap.google}
        </Button>
      )}
      {oauthOptions?.apple && (
        <Button
          onClick={authWithProvider({
            product,
            flowEvent,
            setError,
            provider: 'apple',
            mode,
            trackAuthStepStarted,
          })}
          appearance="neutral"
          mode="plain"
          size="large"
          className={styles.oauthButton}
          startIcon={getProviderIcon('apple')}
        >
          {providerTextMap.apple}
        </Button>
      )}
      {oauthOptions?.facebook && (
        <Button
          onClick={authWithProvider({
            product,
            flowEvent,
            setError,
            provider: 'facebook',
            mode,
            trackAuthStepStarted,
          })}
          appearance="neutral"
          mode="plain"
          size="large"
          className={styles.oauthButton}
          startIcon={getProviderIcon('facebook')}
        >
          {providerTextMap.facebook}
        </Button>
      )}
      {oauthOptions && Object.values(oauthOptions).some(Boolean) && (
        <div className={styles.oauthOtpDivider}>
          <Divider>{translate('Or')}</Divider>
        </div>
      )}
    </Stack>
  );
}

export default OauthSection;
